import {
  Mail, MessageCircle, MessageSquareText, Phone, PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing,
  Smartphone, StickyNote, type LucideIcon,
} from "lucide-react";
import {
  CHANNEL_LABELS, type ActivityType, type CallActivity, type CallStatus, type Channel,
} from "@/lib/conversations";
import { cn } from "@/lib/utils";

/**
 * How each channel and each kind of call looks, in one place, so the list, the timeline and the
 * composer can never draw the same thing two ways. Lucide has no brand marks, so the channels are
 * told apart by shape and colour and always carry their name beside or under the icon.
 */

export const CHANNEL_ICONS: Record<Channel, LucideIcon> = {
  sms: MessageSquareText,
  whatsapp: MessageCircle,
  imessage: Smartphone,
  email: Mail,
  comment: StickyNote,
};

/** Text colour per channel: SMS in the accent, WhatsApp green, iMessage blue-grey, email neutral,
 *  internal comments in the warning tone every "only the team sees this" thing uses. */
export const CHANNEL_TONES: Record<Channel, string> = {
  sms: "text-accent-11",
  whatsapp: "text-success-11",
  imessage: "text-accent-11",
  email: "text-text-muted",
  comment: "text-warning-11",
};

export function typeIcon(type: ActivityType): LucideIcon {
  return type === "call" ? Phone : CHANNEL_ICONS[type];
}

export function typeLabel(type: ActivityType): string {
  return type === "call" ? "Call" : CHANNEL_LABELS[type];
}

/** The small mark on a list row's avatar: what the latest activity was. */
export function TypeMark({ type, className }: { type: ActivityType; className?: string }) {
  const Icon = typeIcon(type);
  return (
    <span
      className={cn(
        "flex size-4 items-center justify-center rounded-full bg-surface ring-2 ring-surface",
        type === "call" ? "text-text-muted" : CHANNEL_TONES[type],
        className,
      )}
      title={typeLabel(type)}
    >
      <Icon className="size-3" aria-hidden />
      <span className="sr-only">{typeLabel(type)}</span>
    </span>
  );
}

/** A call's icon and tone: which way it went, and whether anybody talked. */
export function callLook(a: Pick<CallActivity, "direction" | "status">): { icon: LucideIcon; tone: string } {
  const bad: Record<CallStatus, string> = {
    completed: "text-success-11",
    missed: "text-danger-11",
    no_answer: "text-warning-11",
    busy: "text-warning-11",
    failed: "text-danger-11",
  };
  if (a.status === "completed") {
    return { icon: a.direction === "inbound" ? PhoneIncoming : PhoneOutgoing, tone: bad.completed };
  }
  if (a.status === "missed" || a.status === "no_answer") return { icon: PhoneMissed, tone: bad[a.status] };
  return { icon: PhoneOff, tone: bad[a.status] };
}

export const CALL_STATUS_TONES: Record<CallStatus, "success" | "warning" | "danger"> = {
  completed: "success",
  missed: "danger",
  no_answer: "warning",
  busy: "warning",
  failed: "danger",
};
